// Owner name / e-mail for schedule rows, from Django's /api/schema-users/
// (the users of the viewer's own schema).
//
// That call takes ~1.6 s and used to be the last thing every Schedules fetch
// awaited before showing rows. Its answer depends only on the viewer, so one
// lookup per viewer is shared by concurrent fetches and reused for a few
// minutes; SchedulesTable starts it early and still decorates the rows before
// they first render.
//
// Keyed by the viewer's own user id, never shared between users, and plain
// module state, so a reload drops it. A failed lookup is not remembered: this
// fetch skips the decoration, as before, and the next one asks again.

const OWNER_INDEX_TTL_MS = 5 * 60 * 1000;

// userId -> { promise, at }
const ownerIndexByUser = new Map();

function forget(userId, entry) {
  if (ownerIndexByUser.get(userId) === entry) ownerIndexByUser.delete(userId);
}

/**
 * Map(lower-cased user id -> user row) for the schema of `userId`. Rejects
 * when the request itself fails.
 */
export function getOwnerIndex(backendUrl, userId) {
  const hit = ownerIndexByUser.get(userId);
  if (hit && Date.now() - hit.at < OWNER_INDEX_TTL_MS) return hit.promise;

  const entry = { at: Date.now(), promise: null };
  entry.promise = (async () => {
    const res = await fetch(
      `${backendUrl}/api/schema-users/?user_id=${encodeURIComponent(userId)}`,
    );
    const json = await res.json().catch(() => ({}));
    // The view answers 200 with {error, users: []} when its own Supabase
    // reads fail; that is a failed lookup, not an empty schema.
    if (!res.ok || json?.error || !Array.isArray(json?.users)) forget(userId, entry);
    return new Map(
      (Array.isArray(json.users) ? json.users : [])
        .filter((u) => u?.id)
        .map((u) => [String(u.id).toLowerCase(), u]),
    );
  })();
  // Also marks the rejection handled for a caller that returns before
  // awaiting it (a superseded fetch).
  entry.promise.catch(() => forget(userId, entry));
  ownerIndexByUser.set(userId, entry);
  return entry.promise;
}

/** Test hook: forget every viewer's lookup. */
export function _resetOwnerIndexForTests() {
  ownerIndexByUser.clear();
}
